/**
 * WebsiteAuditScene — "Browser inspector" animation for the "Auditing Website" stage.
 */
import { useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle, XCircle, Globe, Shield, ShieldOff, Smartphone, Code2, MapPin, Phone, Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { BRAND } from '../../config'
import type { WebsiteAuditSceneData } from '../../types'

// ── Helpers ───────────────────────────────────────────────────────────────────

function SignalRow({
  icon,
  label,
  value,
  positive,
  delay = 0,
}: {
  icon: React.ReactNode
  label: string
  value?: string
  positive: boolean
  delay?: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.35, ease: 'easeOut' }}
      className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0"
    >
      <div className="mt-0.5 shrink-0">
        {positive ? (
          <CheckCircle className="w-4 h-4 text-green-500" />
        ) : (
          <XCircle className="w-4 h-4 text-red-400" />
        )}
      </div>
      <div className="flex items-center gap-2 w-36 shrink-0">
        <span className="text-gray-400">{icon}</span>
        <span className="text-xs font-medium text-gray-600">{label}</span>
      </div>
      {value !== undefined && (
        <span className="text-xs text-gray-500 truncate flex-1">{value}</span>
      )}
    </motion.div>
  )
}

function TypewriterText({ text, delay = 0 }: { text: string; delay?: number }) {
  return (
    <motion.span
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay, duration: 0.5 }}
      className="inline-block"
    >
      {text}
    </motion.span>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

interface WebsiteAuditSceneProps {
  data: WebsiteAuditSceneData | null
}

export function WebsiteAuditScene({ data }: WebsiteAuditSceneProps) {
  const { t } = useTranslation('scan')

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-400">
        <motion.div
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
          className="text-5xl"
        >
          🌐
        </motion.div>
        <p className="text-sm font-medium">{t('scene.website.fetching')}</p>
        <div className="flex gap-1.5">
          {[0, 1, 2].map(i => (
            <motion.span
              key={i}
              className="w-2 h-2 rounded-full bg-gray-300"
              animate={{ scale: [1, 1.4, 1] }}
              transition={{ repeat: Infinity, delay: i * 0.2, duration: 0.7 }}
            />
          ))}
        </div>
      </div>
    )
  }

  const hasWebsite = !!data.website_url
  const displayURL = data.website_url ?? (data.business_name ? `${data.business_name} (no website)` : '—')
  const responseMs = typeof data.response_time_ms === 'number' ? data.response_time_ms : null
  const schemaList = data.schema_types?.join(', ') || null

  const signals = useMemo(() => {
    const list: Array<{
      key: string
      icon: React.ReactNode
      label: string
      value?: string
      positive: boolean
    }> = []

    if (hasWebsite) {
      list.push({
        key: 'ssl',
        icon: data.has_ssl ? <Shield className="w-3.5 h-3.5" /> : <ShieldOff className="w-3.5 h-3.5" />,
        label: t('scene.website.ssl.label'),
        value: data.has_ssl ? t('scene.website.ssl.secure') : t('scene.website.ssl.notSecured'),
        positive: !!data.has_ssl,
      })

      if (responseMs !== null) {
        list.push({
          key: 'speed',
          icon: <Globe className="w-3.5 h-3.5" />,
          label: t('scene.website.speed.label'),
          value: t('scene.website.speed.ms', { ms: responseMs.toLocaleString() }),
          positive: responseMs < 2000,
        })
      }

      list.push({
        key: 'mobile',
        icon: <Smartphone className="w-3.5 h-3.5" />,
        label: t('scene.website.mobile.label'),
        value: data.has_mobile_viewport ? t('scene.website.mobile.configured') : t('scene.website.mobile.missing'),
        positive: !!data.has_mobile_viewport,
      })

      list.push({
        key: 'schema',
        icon: <Code2 className="w-3.5 h-3.5" />,
        label: t('scene.website.schema.label'),
        value: schemaList ?? t('scene.website.schema.noneDetected'),
        positive: !!data.has_local_schema,
      })
    }

    if (data.has_address_signal !== undefined) {
      list.push({
        key: 'address',
        icon: <MapPin className="w-3.5 h-3.5" />,
        label: t('scene.website.address.label'),
        value: data.has_address_signal ? t('scene.website.address.found') : t('scene.website.address.notFound'),
        positive: !!data.has_address_signal,
      })
    }

    if (data.has_phone_signal !== undefined) {
      list.push({
        key: 'phone',
        icon: <Phone className="w-3.5 h-3.5" />,
        label: t('scene.website.phone.label'),
        value: data.has_phone_signal ? t('scene.website.phone.found') : t('scene.website.phone.notFound'),
        positive: !!data.has_phone_signal,
      })
    }

    return list
  }, [data, hasWebsite, responseMs, schemaList, t])

  return (
    <div className="flex flex-col h-full gap-4 max-w-xl mx-auto w-full">
      {/* Business identity card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4"
      >
        <div className="shrink-0">
          {data.logo_url ? (
            <img
              src={data.logo_url}
              alt={data.business_name}
              className="w-12 h-12 rounded-xl object-cover border border-gray-100"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          ) : (
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-xl"
              style={{ background: `linear-gradient(135deg, ${BRAND.navy}, #1e3a8a)` }}
            >
              🏢
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-gray-900 truncate">
            <TypewriterText text={data.business_name ?? t('scene.website.unknown')} delay={0.1} />
          </h3>
          {data.category && (
            <p className="text-xs text-gray-500 mt-0.5">
              <TypewriterText text={data.category} delay={0.3} />
            </p>
          )}
          {data.address && (
            <p className="text-xs text-gray-400 mt-0.5 truncate">
              <TypewriterText text={data.address} delay={0.5} />
            </p>
          )}
        </div>

        {data.rating !== undefined && data.rating > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, type: 'spring' }}
            className="shrink-0 flex flex-col items-center gap-0.5"
          >
            <div className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
              <span className="text-sm font-bold text-gray-800">{data.rating.toFixed(1)}</span>
            </div>
            {data.is_verified && (
              <span className="text-xs text-green-600 font-medium flex items-center gap-0.5">
                <CheckCircle className="w-3 h-3" /> {t('scene.website.verified')}
              </span>
            )}
          </motion.div>
        )}
      </motion.div>

      {/* Browser chrome mockup */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex-1"
      >
        {/* Browser title bar */}
        <div className="bg-gray-50 border-b border-gray-100 px-4 py-2.5 flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-yellow-400" />
            <div className="w-3 h-3 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-1 flex items-center gap-2 min-w-0">
            <Globe className="w-3 h-3 text-gray-400 shrink-0" />
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-xs text-gray-600 font-mono truncate"
            >
              {displayURL}
            </motion.span>
            {data.has_ssl && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
                <Shield className="w-3 h-3 text-green-500 shrink-0" />
              </motion.div>
            )}
          </div>
        </div>

        {/* Inspector panel */}
        <div className="p-4 overflow-y-auto flex-1">
          <AnimatePresence>
            {data.page_title && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="mb-3"
              >
                <span className="text-xs uppercase tracking-wider text-gray-400 font-medium">{t('scene.website.pageTitle')}</span>
                <p className="text-sm font-semibold text-gray-800 mt-0.5 line-clamp-2">
                  {data.page_title}
                </p>
              </motion.div>
            )}

            {data.h1 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0 }}
                className="mb-3"
              >
                <span className="text-xs uppercase tracking-wider text-gray-400 font-medium">{t('scene.website.h1Heading')}</span>
                <p className="text-sm text-gray-700 mt-0.5 line-clamp-1">{data.h1}</p>
              </motion.div>
            )}

            {data.meta_description && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                className="mb-4"
              >
                <span className="text-xs uppercase tracking-wider text-gray-400 font-medium">{t('scene.website.metaDesc')}</span>
                <p className="text-xs text-gray-600 mt-0.5 line-clamp-3 leading-relaxed">
                  {data.meta_description}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {signals.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4 }}
              className="border-t border-gray-50 pt-3"
            >
              <span className="text-xs uppercase tracking-wider text-gray-400 font-medium block mb-2">
                {t('scene.website.seoSignals')}
              </span>
              <div>
                {signals.map((signal, i) => (
                  <SignalRow
                    key={signal.key}
                    icon={signal.icon}
                    label={signal.label}
                    value={signal.value}
                    positive={signal.positive}
                    delay={1.5 + i * 0.15}
                  />
                ))}
              </div>
            </motion.div>
          )}

          {!hasWebsite && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0 }}
              className="flex flex-col items-center gap-2 py-8 text-center"
            >
              <XCircle className="w-8 h-8 text-red-300" />
              <p className="text-sm font-medium text-gray-600">{t('scene.website.noWebsite')}</p>
              <p className="text-xs text-gray-400">{t('scene.website.noWebsiteDesc')}</p>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  )
}
